import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "@hooks/useTheme";
import CustomSelect from "@components/UI/CustomSelect";
import { CustomTextarea } from "@components/Forms";
import { PrimaryButton } from "@components/Buttons";
import { DownloadIcon, DropIcon } from "@components/Icons";
import { VEX_STATUS } from "@constants/vex";
import { downloadFile } from "@utils/common";

const NOTES_MAX = 1000;
const MAX_FILE_BYTES = 20 * 1024 * 1024;

const getDownloadLink = (fileInfoSeq) => `/download/${fileInfoSeq}`;

const CveReviewPanel = ({
  form,
  onChange,
  statusOptions = [],
  justificationOptions = [],
  existingAttachments = [],
  onDeleteExistingAttachment,
  deletingAttachmentId = null,
  onApprove,
  approving = false,
  disabled = false,
  initialForm = null,
  "data-testid": dataTestId = "cve-review-panel",
}) => {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const fileInputRef = useRef(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [fileError, setFileError] = useState("");

  const selectedStatus = useMemo(
    () => statusOptions.find((opt) => opt.value === form.vexStatus) || null,
    [form.vexStatus, statusOptions],
  );
  const selectedJustification = useMemo(
    () => justificationOptions.find((opt) => opt.value === form.justification) || null,
    [form.justification, justificationOptions],
  );

  const notesLength = form.additionalNote?.length || 0;
  const showJustification = form.vexStatus === VEX_STATUS.NOT_AFFECTED;
  const pendingFiles = form.files || [];

  // Enable Approve only when the user actually changed something from the persisted row
  // (including newly attached files that are pending upload).
  const isDirty = useMemo(() => {
    if (!initialForm) return true;
    if (pendingFiles.length > 0) return true;
    const base = { vexStatus: "", justification: "", additionalNote: "" };
    const initial = { ...base, ...initialForm };
    return (
      (form.vexStatus || "") !== (initial.vexStatus || "") ||
      (form.justification || "") !== (initial.justification || "") ||
      (form.additionalNote || "") !== (initial.additionalNote || "")
    );
  }, [form.vexStatus, form.justification, form.additionalNote, pendingFiles.length, initialForm]);

  // Mirror BE validation: NOT_AFFECTED requires a justification.
  const isFormValid = useMemo(() => {
    if (!form.vexStatus) return false;
    if (form.vexStatus === VEX_STATUS.NOT_AFFECTED && !form.justification) return false;
    if ((form.additionalNote || "").length > NOTES_MAX) return false;
    return true;
  }, [form.vexStatus, form.justification, form.additionalNote]);

  const selectMenuStyles = {
    menuPortal: (base) => ({ ...base, zIndex: 10050 }),
    menu: (base) => ({ ...base, zIndex: 10050 }),
  };

  const updateField = (patch) => {
    onChange({ ...form, ...patch });
  };

  const handleStatusChange = (opt) => {
    const nextStatus = opt?.value || "";
    updateField({
      vexStatus: nextStatus,
      justification: nextStatus === VEX_STATUS.NOT_AFFECTED ? form.justification : "",
    });
  };

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (incoming.length === 0) return;

    const oversized = incoming.find((file) => file.size > MAX_FILE_BYTES);
    if (oversized) {
      setFileError(t("status.vex-attachment-max-size"));
      return;
    }
    setFileError("");

    const mapped = incoming.map((file, index) => ({
      id: `${Date.now()}-${index}`,
      name: file.name,
      size: file.size,
      type: file.type,
      file,
    }));

    updateField({
      files: [...pendingFiles, ...mapped],
    });
  };

  const removeFile = (fileId) => {
    updateField({
      files: pendingFiles.filter((f) => f.id !== fileId),
    });
  };

  return (
    <div
      className={`flex h-full min-h-0 flex-col rounded-lg border ${colors.borderPrimary} ${colors.bgModal}`}
      data-testid={dataTestId}
    >
      <div className="px-4 py-3 flex-shrink-0">
        <h3 className={`text-sm font-semibold tracking-wide uppercase ${colors.textPrimary}`}>
          {t("status.vex-review")}
        </h3>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-4 space-y-4">
        <div>
          <label className={`block text-sm mb-1.5 ${colors.textSecondary}`}>{t("status.vex-status")}</label>
          <CustomSelect
            options={statusOptions}
            value={selectedStatus}
            onChange={handleStatusChange}
            placeholder={t("status.vex-status-placeholder")}
            clearable={false}
            disabled={disabled}
            usePortal
            menuPlacement="bottom"
            customStyles={selectMenuStyles}
            data-testid={`${dataTestId}-status`}
          />
        </div>

        {showJustification && (
          <div>
            <label className={`block text-sm mb-1.5 ${colors.textSecondary}`}>
              {t("status.vex-justification-required")}
              <span className={colors.textRequired}>*</span>
            </label>
            <CustomSelect
              options={justificationOptions}
              value={selectedJustification}
              onChange={(opt) => updateField({ justification: opt?.value || "" })}
              placeholder={t("status.vex-justification-placeholder")}
              clearable
              disabled={disabled}
              usePortal
              menuPlacement="bottom"
              customStyles={selectMenuStyles}
              data-testid={`${dataTestId}-justification`}
            />
          </div>
        )}

        <div>
          <label className={`block text-sm mb-1.5 ${colors.textSecondary}`}>
            {t("status.vex-additional-notes-optional")}
          </label>
          <div className="relative">
            <CustomTextarea
              value={form.additionalNote || ""}
              onChange={(e) => {
                const next = e.target.value.slice(0, NOTES_MAX);
                updateField({ additionalNote: next });
              }}
              placeholder={t("status.vex-additional-notes-placeholder")}
              rows={4}
              disabled={disabled}
              data-testid={`${dataTestId}-notes`}
            />
            <div className={`absolute bottom-2 right-3 text-xs ${colors.textSecondary}`}>
              {notesLength} / {NOTES_MAX}
            </div>
          </div>
        </div>

        <div>
          <label className={`block text-sm mb-1.5 ${colors.textSecondary}`}>
            {t("status.vex-attachments-optional")}
          </label>

          {existingAttachments.length > 0 && (
            <ul className="mb-2 space-y-1" data-testid={`${dataTestId}-existing-file-list`}>
              {existingAttachments.map((file) => {
                const name = file.realFileName || file.fileName || file.name || "-";
                const key = file.fileInfoSeq || name;
                return (
                  <li
                    key={key}
                    className={`flex items-center justify-between gap-2 rounded border px-2 py-1 text-xs ${colors.borderPrimary}`}
                  >
                    <span className={`truncate ${colors.textPrimary}`} title={name}>
                      {name}
                    </span>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {file.fileInfoSeq != null && (
                        <button
                          type="button"
                          className={`${colors.textActive} hover:opacity-80`}
                          onClick={() =>
                            downloadFile(
                              file.realFileName || file.serverFileName || name,
                              getDownloadLink(file.fileInfoSeq),
                            )
                          }
                          title={t("common.download", "Download")}
                          aria-label={t("common.download", "Download")}
                          data-testid={`${dataTestId}-download-file-${file.fileInfoSeq}`}
                        >
                          <DownloadIcon className="h-4 w-4" />
                        </button>
                      )}
                      {!disabled && file.fileInfoSeq != null && onDeleteExistingAttachment && (
                        <button
                          type="button"
                          className="text-red-400 hover:text-red-300 disabled:opacity-50"
                          onClick={() => onDeleteExistingAttachment(file.fileInfoSeq)}
                          disabled={deletingAttachmentId === file.fileInfoSeq}
                          data-testid={`${dataTestId}-delete-file-${file.fileInfoSeq}`}
                        >
                          {t("common.delete-button")}
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {pendingFiles.length > 0 && (
            <ul className="mb-2 space-y-1" data-testid={`${dataTestId}-file-list`}>
              {pendingFiles.map((file) => (
                <li
                  key={file.id}
                  className={`flex items-center justify-between gap-2 rounded border px-2 py-1 text-xs ${colors.borderPrimary}`}
                >
                  <span className={`truncate ${colors.textPrimary}`}>{file.name}</span>
                  {!disabled && (
                    <button
                      type="button"
                      className="text-red-400 hover:text-red-300 flex-shrink-0"
                      onClick={() => removeFile(file.id)}
                      data-testid={`${dataTestId}-remove-file-${file.id}`}
                    >
                      {t("common.delete-button")}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {!disabled && (
            <div
              data-testid={`${dataTestId}-drop-zone`}
              className={`border border-dashed rounded-lg px-3 py-6 text-center transition-colors cursor-pointer ${
                isDragOver ? "border-blue-400 bg-blue-500/10" : colors.borderPrimary
              }`}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragOver(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                setIsDragOver(false);
              }}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragOver(false);
                addFiles(e.dataTransfer.files);
              }}
              onClick={() => fileInputRef.current?.click()}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
            >
              <DropIcon className={`mx-auto h-8 w-8 mb-2 ${colors.textActive || "text-blue-400"}`} />
              <p className={`text-sm ${colors.textPrimary}`}>{t("status.vex-attachment-hint")}</p>
              <p className={`text-xs mt-1 ${colors.textSecondary}`}>{t("status.vex-attachment-max-size")}</p>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={(e) => {
                  addFiles(e.target.files);
                  e.target.value = "";
                }}
                data-testid={`${dataTestId}-file-input`}
              />
            </div>
          )}
          {fileError && (
            <p className="mt-1 text-xs text-red-400" data-testid={`${dataTestId}-file-error`}>
              {fileError}
            </p>
          )}
        </div>
      </div>

      {!disabled && (
        <div className="px-4 py-3 flex justify-end flex-shrink-0">
          <PrimaryButton
            onClick={onApprove}
            disabled={approving || !isDirty || !isFormValid}
            data-testid={`${dataTestId}-approve-button`}
          >
            {t("status.vex-approve")}
          </PrimaryButton>
        </div>
      )}
    </div>
  );
};

export default CveReviewPanel;
